import { useAuth } from "@clerk/react";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  downloadBlueprintPdf,
  rateBlueprint,
  recordBlueprintDownloaded,
  recordBuildPromptCopied,
} from "@/features/blueprints/blueprintApi";
import {
  createBuildPromptMarkdown,
  createBuildPromptMarkdownFileName,
  createBlueprintMarkdown,
  createBlueprintMarkdownFileName,
} from "@/features/blueprints/blueprintMarkdown";
import {
  parseDataModelItem,
  parseEdgeCase,
  parseInterfaceScreens,
  parseTestScenario,
} from "@/features/blueprints/blueprintParsers";
import { detectIncompleteBuildPrompt } from "@shared/buildPromptQuality";
import {
  TARGET_BUILDERS,
  type Blueprint,
  type BlueprintLockedSection,
  type BlueprintOutputAccess,
  type BlueprintStatus,
  type GeneratedBlueprintOutput,
  type TargetBuilder,
} from "@/features/blueprints/blueprintTypes";

type GeneratedBlueprintViewProps = {
  blueprintId: string;
  title: string;
  output: GeneratedBlueprintOutput;
  targetBuilder?: TargetBuilder;
  generationVersion?: number;
  status?: BlueprintStatus;
  generatedAt?: string;
  rating?: number | null;
  ratingFeedback?: string | null;
  outputAccess?: BlueprintOutputAccess;
  onBlueprintUpdated?: (blueprint: Blueprint) => void;
  projectName?: string | null;
  inputText?: string;
  onRegenerate?: () => void | Promise<void>;
  isRegenerating?: boolean;
};

type BlueprintTab =
  | "summary"
  | "testingNotes"
  | "overview"
  | "vibe"
  | "data"
  | "acceptance"
  | "edgeCases"
  | "tests"
  | "prompt";

const tabs: Array<{ id: BlueprintTab; label: string; description: string }> = [
  { id: "summary", label: "Blueprint", description: "Solution overview" },
  {
    id: "prompt",
    label: "Master Build Prompt",
    description: "Builder instruction",
  },
  { id: "testingNotes", label: "Testing Notes", description: "Build checks" },
];

const BUILD_PROMPT_PREVIEW_CHARS = 2000;
const SUMMARY_TEXT_PREVIEW_CHARS = 900;

function shortenSectionPreview(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length <= SUMMARY_TEXT_PREVIEW_CHARS) return trimmed;
  return `${trimmed.slice(0, SUMMARY_TEXT_PREVIEW_CHARS).trimEnd()}…`;
}

function normalizeBuildPromptPreviewHeading(value: string): string {
  return value.replace(
    /^(#\s*)V\d+-RC\s+Master Implementation Build Prompt\b/i,
    "$1VIBE-RC Master Implementation Build Prompt",
  );
}

function cleanVisibleBlueprintText(value: string, maxLines = 12): string[] {
  const cleaned = value
    .replace(/\*\*/g, "")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*[-*]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/:\s*$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  if (!cleaned) return [];

  return cleaned
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, maxLines);
}

function BlueprintPreviewText({
  text,
  mode = "paragraph",
  maxItems = 5,
  showMoreNote = true,
}: {
  text: string;
  mode?: "paragraph" | "bullets" | "steps";
  maxItems?: number;
  showMoreNote?: boolean;
}) {
  const allLines = cleanVisibleBlueprintText(text);
  const lines = allLines.slice(0, maxItems);
  const hasMoreDetail =
    allLines.length > lines.length ||
    text.trim().length > SUMMARY_TEXT_PREVIEW_CHARS;

  if (lines.length === 0) {
    return <EmptyState />;
  }

  if (mode === "paragraph") {
    return (
      <div className="mt-3 space-y-3 text-sm leading-6 text-slate-700">
        {lines.map((line, index) => (
          <p key={`${line}-${index}`}>{line}</p>
        ))}
        {showMoreNote && hasMoreDetail ? (
          <p className="text-xs font-medium text-slate-500">
            Full detail is available in the downloaded Blueprint.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <ol className="mt-3 space-y-2 text-sm leading-6 text-slate-700">
        {lines.map((line, index) => (
          <li
            key={`${line}-${index}`}
            className="grid min-w-0 grid-cols-[1.75rem_minmax(0,1fr)] gap-2"
          >
            <span
              className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ${
                mode === "steps"
                  ? "bg-indigo-100 text-indigo-700"
                  : "bg-slate-200 text-slate-600"
              }`}
            >
              {mode === "steps" ? index + 1 : "•"}
            </span>
            <span className="min-w-0 break-words">{line}</span>
          </li>
        ))}
      </ol>
      {showMoreNote && hasMoreDetail ? (
        <p className="mt-3 text-xs font-medium text-slate-500">
          Full detail is available in the downloaded Blueprint.
        </p>
      ) : null}
    </>
  );
}

function createMarkdownFileName(
  title: string,
  suffix: string,
  date = new Date(),
): string {
  const safeTitle = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

  if (safeTitle) return `${safeTitle}-${suffix}.md`;
  return `alchemy-${suffix}-${date.toISOString().slice(0, 10)}.md`;
}

function formatTestingNotesMarkdownSection(
  title: string,
  items: string[],
  locked: boolean,
): string[] {
  if (locked) {
    return [`## ${title}`, "", "_Detailed content is included with Pro._", ""];
  }

  if (items.length === 0) {
    return [
      `## ${title}`,
      "",
      "_No items were returned for this section._",
      "",
    ];
  }

  return [`## ${title}`, "", ...items.map((item) => `- ${item.trim()}`), ""];
}

const proOnlyTabSections: Partial<
  Record<BlueprintTab, BlueprintLockedSection>
> = {
  acceptance: "acceptanceCriteria",
  edgeCases: "edgeCases",
  tests: "testScenarios",
};

const vibeSections: Array<{
  key: keyof GeneratedBlueprintOutput["vibeRcSpec"];
  label: string;
  description: string;
}> = [
  {
    key: "vision",
    label: "Product Goal",
    description:
      "The audience, problem and outcome this app is designed around.",
  },
  {
    key: "interface",
    label: "Screens & User Experience",
    description:
      "The screens, layout, actions and responsive behaviour the first version should include.",
  },
  {
    key: "behavior",
    label: "App Workflows",
    description:
      "The key journeys, rules and interactions that should happen inside the app.",
  },
  {
    key: "examples",
    label: "Examples & Sample Data",
    description:
      "Realistic scenarios, records and patterns that make the build more concrete.",
  },
  {
    key: "role",
    label: "Builder Guidance",
    description:
      "How the AI builder should approach trade-offs, simplicity and implementation quality.",
  },
  {
    key: "constraints",
    label: "Version 1 Boundaries",
    description:
      "What should stay out of the first build so the MVP remains focused.",
  },
];

function formatTargetBuilder(value?: TargetBuilder): string | null {
  if (!value) {
    return null;
  }

  return (
    TARGET_BUILDERS.find((builder) => builder.value === value)?.label ?? value
  );
}

function formatDate(value?: string | null): string | null {
  if (!value) {
    return null;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

// ─── Display-only helper — data model category detection ──────────────────────
// Detects semantic category from entity name for subtle visual differentiation.
// Pure function, no schema or generation impact.

type DataModelCategoryInfo = {
  label: string;
  cardClasses: string;
  labelClasses: string;
};

function getDataModelCategoryInfo(name: string): DataModelCategoryInfo | null {
  if (/(validation|rules|constraint)/i.test(name)) {
    return {
      label: "validation",
      cardClasses: "border-amber-200 bg-amber-50",
      labelClasses: "bg-amber-100 text-amber-700",
    };
  }
  if (/(sample|example)/i.test(name)) {
    return {
      label: "sample data",
      cardClasses: "border-sky-200 bg-sky-50",
      labelClasses: "bg-sky-100 text-sky-700",
    };
  }
  if (/(system|metadata|audit|timestamp)/i.test(name)) {
    return {
      label: "system",
      cardClasses: "border-slate-200 bg-slate-50",
      labelClasses: "bg-slate-200 text-slate-600",
    };
  }
  if (/(status values|status|enum)/i.test(name)) {
    return {
      label: "status",
      cardClasses: "border-violet-200 bg-violet-50",
      labelClasses: "bg-violet-100 text-violet-700",
    };
  }
  return null;
}

// ─── Base display components ──────────────────────────────────────────────────

function EmptyState() {
  return (
    <p className="rounded-md bg-slate-50 p-4 text-sm leading-6 text-slate-500">
      No items were returned for this section.
    </p>
  );
}

function TextBlock({ children }: { children: string }) {
  if (!children.trim()) {
    return <EmptyState />;
  }

  return (
    <div className="whitespace-pre-wrap text-sm leading-7 text-slate-700">
      {children}
    </div>
  );
}

function ListSection({ items }: { items: string[] }) {
  if (items.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="grid gap-3">
      {items.map((item, index) => (
        <div
          key={`${item}-${index}`}
          className="rounded-md border border-slate-200 bg-white p-4 shadow-sm"
        >
          <p className="whitespace-pre-wrap text-sm leading-7 text-slate-700">
            {item}
          </p>
        </div>
      ))}
    </div>
  );
}

function ConcisePreviewList({
  items,
  maxItems = 4,
}: {
  items: string[];
  maxItems?: number;
}) {
  if (items.length === 0) return <EmptyState />;

  return (
    <ul className="space-y-2 text-sm leading-6 text-slate-700">
      {items.slice(0, maxItems).map((item, index) => (
        <li key={`${item}-${index}`} className="flex min-w-0 gap-2">
          <span
            aria-hidden="true"
            className="shrink-0 font-semibold text-indigo-500"
          >
            •
          </span>
          <span className="min-w-0 break-words">
            {shortenSectionPreview(item)}
          </span>
        </li>
      ))}
      {items.length > maxItems ? (
        <li className="text-xs font-medium text-slate-500">
          + {items.length - maxItems} more in the downloaded Blueprint
        </li>
      ) : null}
    </ul>
  );
}

function LockedProSection({ description }: { description: string }) {
  return (
    <div className="rounded-lg border border-dashed border-indigo-200 bg-indigo-50 p-6">
      <p className="text-sm font-semibold text-indigo-950">Included with Pro</p>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-indigo-900">
        {description}
      </p>
      <Link
        to="/pricing"
        className="mt-4 inline-flex rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
      >
        Upgrade to Pro
      </Link>
    </div>
  );
}

// ─── Count badge — subtle pill showing item count ─────────────────────────────

function CountBadge({ count, label }: { count: number; label: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
      {count} {label}
    </span>
  );
}

// ─── Section heading — with optional count badge ──────────────────────────────

function SectionHeading({
  title,
  description,
  count,
  countLabel,
}: {
  title: string;
  description?: string;
  count?: number;
  countLabel?: string;
}) {
  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-2">
        <h3 className="text-xl font-semibold tracking-normal text-slate-950">
          {title}
        </h3>
        {typeof count === "number" && countLabel ? (
          <CountBadge count={count} label={countLabel} />
        ) : null}
      </div>
      {description ? (
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
          {description}
        </p>
      ) : null}
    </div>
  );
}

function TabGuidance({ children }: { children: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
      {children}
    </div>
  );
}

// ─── Copy button — section-level copy ────────────────────────────────────────

function CopySectionButton({
  text,
  darkMode,
}: {
  text: string;
  darkMode?: boolean;
}) {
  const [state, setState] = useState<"idle" | "copied" | "error">("idle");

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setState("copied");
      window.setTimeout(() => setState("idle"), 2000);
    } catch {
      setState("error");
      window.setTimeout(() => setState("idle"), 2000);
    }
  }

  const label =
    state === "copied" ? "Copied" : state === "error" ? "Failed" : "Copy";

  if (darkMode) {
    return (
      <button
        type="button"
        onClick={() => void handleCopy()}
        className="shrink-0 rounded px-2 py-1 text-xs font-semibold text-slate-500 transition hover:bg-slate-800 hover:text-slate-300"
      >
        {label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void handleCopy()}
      className="shrink-0 rounded px-2 py-1 text-xs font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
    >
      {label}
    </button>
  );
}

// ─── ScreenList — Screens & User Experience with optional screen card parsing ──

function ScreenList({ text }: { text: string }) {
  const parsed = parseInterfaceScreens(text);

  if (parsed.type === "plain") {
    return <TextBlock>{text}</TextBlock>;
  }

  return (
    <div className="grid gap-3">
      {parsed.screens.map((screen, index) => (
        <div
          key={`${screen.name}-${index}`}
          className="rounded-md border border-slate-200 bg-white p-4 shadow-sm"
        >
          <p className="mb-2 text-sm font-semibold text-slate-900">
            {screen.name}
          </p>
          {screen.content ? (
            <div className="whitespace-pre-wrap text-xs leading-6 text-slate-600">
              {screen.content}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

// ─── Acceptance Criteria — numbered pass/fail checklist ───────────────────────

function ChecklistList({ items }: { items: string[] }) {
  if (items.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="grid gap-2.5">
      {items.map((item, index) => (
        <div
          key={`${item}-${index}`}
          className="flex gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4"
        >
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-xs font-bold text-white">
            {index + 1}
          </span>
          <p className="text-sm leading-7 text-slate-700">{item}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Edge Cases — situation / handling cards with optional split ──────────────

function EdgeCaseList({ items }: { items: string[] }) {
  if (items.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="grid gap-3">
      {items.map((item, index) => {
        const parsed = parseEdgeCase(item);
        return (
          <div
            key={`${item}-${index}`}
            className="rounded-md border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="mb-3">
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500">
                Case {index + 1}
              </span>
            </div>
            {parsed.type === "split" ? (
              <div className="space-y-3">
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Situation
                  </p>
                  <p className="text-sm leading-6 text-slate-700">
                    {parsed.situation}
                  </p>
                </div>
                <div className="border-t border-slate-100 pt-3">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Expected handling
                  </p>
                  <p className="text-sm leading-6 text-slate-700">
                    {parsed.handling}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm leading-7 text-slate-700">{parsed.text}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Test Scenarios — Setup / Action / Expected Result cards ──────────────────

function TestScenarioList({ items }: { items: string[] }) {
  if (items.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="grid gap-4">
      {items.map((item, index) => {
        const parsed = parseTestScenario(item);
        return (
          <div
            key={`${item}-${index}`}
            className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm"
          >
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-2.5">
              <span className="inline-flex items-center rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                Scenario {index + 1}
              </span>
              {parsed.type === "structured" ? (
                <span className="text-xs text-slate-400">
                  Setup · Action · Expected Result
                </span>
              ) : null}
            </div>
            <div className="p-4">
              {parsed.type === "structured" ? (
                <dl className="grid gap-3">
                  <div className="grid grid-cols-[90px_1fr] items-start gap-3">
                    <dt className="pt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Setup
                    </dt>
                    <dd className="text-sm leading-6 text-slate-700">
                      {parsed.setup}
                    </dd>
                  </div>
                  <div className="grid grid-cols-[90px_1fr] items-start gap-3">
                    <dt className="pt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Action
                    </dt>
                    <dd className="text-sm leading-6 text-slate-700">
                      {parsed.action}
                    </dd>
                  </div>
                  <div className="grid grid-cols-[90px_1fr] items-start gap-3 border-t border-slate-100 pt-3">
                    <dt className="pt-0.5 text-xs font-semibold uppercase tracking-wide text-indigo-600">
                      Expected
                    </dt>
                    <dd className="text-sm font-medium leading-6 text-slate-900">
                      {parsed.expectedResult}
                    </dd>
                  </div>
                </dl>
              ) : (
                <p className="text-sm leading-7 text-slate-700">
                  {parsed.text}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Data Model — entity name highlighting with category-aware styling ─────────

function DataModelList({ items }: { items: string[] }) {
  if (items.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="grid gap-3">
      {items.map((item, index) => {
        const parsed = parseDataModelItem(item);

        if (parsed.type === "entity") {
          const categoryInfo = getDataModelCategoryInfo(parsed.name);
          return (
            <div
              key={`${item}-${index}`}
              className={`rounded-md border p-4 shadow-sm ${
                categoryInfo
                  ? categoryInfo.cardClasses
                  : "border-slate-200 bg-white"
              }`}
            >
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-slate-900">
                  {parsed.name}
                </span>
                {parsed.subtitle ? (
                  <span className="rounded bg-white/70 px-1.5 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
                    {parsed.subtitle}
                  </span>
                ) : null}
                {categoryInfo ? (
                  <span
                    className={`rounded px-1.5 py-0.5 text-xs font-medium ${categoryInfo.labelClasses}`}
                  >
                    {categoryInfo.label}
                  </span>
                ) : null}
              </div>
              <p className="break-words font-mono text-xs leading-6 text-slate-600">
                {parsed.body}
              </p>
            </div>
          );
        }

        return (
          <div
            key={`${item}-${index}`}
            className="rounded-md border border-slate-200 bg-white p-4 shadow-sm"
          >
            <p className="text-sm leading-7 text-slate-700">{parsed.text}</p>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main view component ──────────────────────────────────────────────────────

export function GeneratedBlueprintView({
  blueprintId,
  title,
  output,
  targetBuilder,
  status: _status,
  generatedAt,
  rating,
  ratingFeedback,
  outputAccess,
  onBlueprintUpdated,
  projectName,
  inputText,
  onRegenerate,
  isRegenerating = false,
}: GeneratedBlueprintViewProps) {
  const { getToken } = useAuth();
  const [activeTab, setActiveTab] = useState<BlueprintTab>("summary");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">(
    "idle",
  );
  const [testingNotesCopyState, setTestingNotesCopyState] = useState<
    "idle" | "copied" | "error"
  >("idle");
  const [testingNotesDownloadState, setTestingNotesDownloadState] = useState<
    "idle" | "downloaded" | "error"
  >("idle");
  const [, setPromptDownloadState] = useState<"idle" | "downloaded" | "error">(
    "idle",
  );
  const [exportError, setExportError] = useState<string | null>(null);
  const [pdfState, setPdfState] = useState<"idle" | "loading" | "error">(
    "idle",
  );
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [trackingWarning, setTrackingWarning] = useState<string | null>(null);
  const [selectedRating, setSelectedRating] = useState<number | null>(
    rating ?? null,
  );
  const [feedback, setFeedback] = useState(ratingFeedback ?? "");
  const [ratingState, setRatingState] = useState<
    "idle" | "saving" | "saved" | "error"
  >(rating ? "saved" : "idle");

  useEffect(() => {
    setSelectedRating(rating ?? null);
    setFeedback(ratingFeedback ?? "");
    setRatingState(rating ? "saved" : "idle");
  }, [rating, ratingFeedback]);

  const metadata = useMemo(() => {
    const promptWords = output.builderReadyPrompt
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
    const items = [
      { label: "Product", value: title },
      { label: "Builder", value: formatTargetBuilder(targetBuilder) },
      { label: "Generated", value: formatDate(generatedAt) },
      {
        label: "Build quality",
        value:
          outputAccess?.generationQuality === "deep"
            ? "Deep Build Prompt"
            : "Standard Build Prompt",
      },
      {
        label: "Prompt length",
        value: `${promptWords.toLocaleString()} words`,
      },
    ];

    return items.filter((item): item is { label: string; value: string } =>
      Boolean(item.value),
    );
  }, [
    generatedAt,
    output.builderReadyPrompt,
    outputAccess?.generationQuality,
    targetBuilder,
    title,
  ]);

  // Parse the Build Prompt into named sections for structured rendering.
  // Returns [] if fewer than 3 sections detected — raw <pre> fallback is used then.
  const incompletePrompt = useMemo(
    () => detectIncompleteBuildPrompt(output.builderReadyPrompt),
    [output.builderReadyPrompt],
  );
  const buildPromptPreview = useMemo(
    () =>
      normalizeBuildPromptPreviewHeading(
        output.builderReadyPrompt.slice(0, BUILD_PROMPT_PREVIEW_CHARS),
      ),
    [output.builderReadyPrompt],
  );
  const buildPromptPreviewLines = useMemo(
    () => buildPromptPreview.split("\n"),
    [buildPromptPreview],
  );

  const targetBuilderLabel = formatTargetBuilder(targetBuilder);
  const lockedSections = new Set(outputAccess?.lockedSections ?? []);
  const canExportPdf = outputAccess?.outputTier === "pro";
  const isProOutput = outputAccess?.canViewProSections === true;
  const constraintsPreviewText = useMemo(() => {
    const seen = new Set<string>();
    return [
      ...cleanVisibleBlueprintText(output.vibeRcSpec.constraints),
      ...output.wontBuildYet.flatMap((item) =>
        cleanVisibleBlueprintText(item, 2),
      ),
    ]
      .filter((item) => {
        const key = item.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .join("\n");
  }, [output.vibeRcSpec.constraints, output.wontBuildYet]);

  function isTabLocked(tabId: BlueprintTab): boolean {
    const section = proOnlyTabSections[tabId];
    return Boolean(section && lockedSections.has(section));
  }

  function createTestingNotesMarkdown(): string {
    return [
      `# ${title.trim() || "Alchemy Blueprint"} - Testing Notes`,
      "",
      generatedAt ? `- Generated: ${formatDate(generatedAt)}` : null,
      targetBuilderLabel ? `- Target builder: ${targetBuilderLabel}` : null,
      "- Use: Copy these checks into your QA notes before accepting the finished build.",
      "",
      ...formatTestingNotesMarkdownSection(
        "Acceptance Criteria",
        output.acceptanceCriteria,
        isTabLocked("acceptance"),
      ),
      ...formatTestingNotesMarkdownSection(
        "Edge Cases",
        output.edgeCases,
        isTabLocked("edgeCases"),
      ),
      ...formatTestingNotesMarkdownSection(
        "Test Scenarios",
        output.testScenarios,
        isTabLocked("tests"),
      ),
    ]
      .filter((part): part is string => part !== null)
      .join("\n")
      .trim()
      .concat("\n");
  }

  async function handleCopyBuildPrompt() {
    if (incompletePrompt.incomplete) {
      setExportError(
        "This Master Build Prompt appears incomplete. Regenerate it before copying or downloading.",
      );
      return;
    }
    try {
      await navigator.clipboard.writeText(output.builderReadyPrompt);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 2200);

      try {
        const token = await getToken();
        const response = await recordBuildPromptCopied(token, blueprintId);
        onBlueprintUpdated?.(response.blueprint);
        setTrackingWarning(null);
      } catch {
        setTrackingWarning(
          "Copied. We could not update the activity timestamp this time.",
        );
      }
    } catch {
      setCopyState("error");
      window.setTimeout(() => setCopyState("idle"), 3000);
    }
  }

  async function handleCopyTestingNotes() {
    if (incompletePrompt.incomplete) {
      setExportError(
        "This Master Build Prompt appears incomplete. Regenerate it before copying testing notes.",
      );
      return;
    }

    try {
      await navigator.clipboard.writeText(createTestingNotesMarkdown());
      setTestingNotesCopyState("copied");
      window.setTimeout(() => setTestingNotesCopyState("idle"), 2200);
      setExportError(null);
    } catch {
      setTestingNotesCopyState("error");
      window.setTimeout(() => setTestingNotesCopyState("idle"), 3000);
    }
  }

  function handleDownloadTestingNotes() {
    if (incompletePrompt.incomplete) {
      setExportError(
        "This Master Build Prompt appears incomplete. Regenerate it before downloading testing notes.",
      );
      return;
    }

    try {
      const markdown = createTestingNotesMarkdown();
      const blob = new Blob([markdown], {
        type: "text/markdown;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = createMarkdownFileName(title, "testing-notes");
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      setTestingNotesDownloadState("downloaded");
      window.setTimeout(() => setTestingNotesDownloadState("idle"), 2200);
      setExportError(null);
    } catch (error) {
      setTestingNotesDownloadState("error");
      setExportError(
        error instanceof Error
          ? error.message
          : "Testing Notes download is unavailable.",
      );
    }
  }

  function handleDownloadBuildPrompt() {
    try {
      const markdown = createBuildPromptMarkdown({
        title,
        generatedAt,
        targetBuilder: targetBuilderLabel,
        builderReadyPrompt: output.builderReadyPrompt,
      });
      const blob = new Blob([markdown], {
        type: "text/markdown;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = createBuildPromptMarkdownFileName(title);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      setExportError(null);
      setPromptDownloadState("downloaded");
      window.setTimeout(() => setPromptDownloadState("idle"), 2200);
    } catch (error) {
      setExportError(
        error instanceof Error
          ? error.message
          : "Master Build Prompt download is unavailable.",
      );
      setPromptDownloadState("error");
    }
  }

  async function handleDownloadMarkdown() {
    try {
      const markdown = createBlueprintMarkdown({
        title,
        generatedAt,
        targetBuilder: targetBuilderLabel,
        output,
        outputAccess,
        projectName,
        inputText,
      });
      const blob = new Blob([markdown], {
        type: "text/markdown;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = createBlueprintMarkdownFileName(title);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      setExportError(null);

      try {
        const token = await getToken();
        const response = await recordBlueprintDownloaded(token, blueprintId);
        onBlueprintUpdated?.(response.blueprint);
        setTrackingWarning(null);
      } catch {
        setTrackingWarning(
          "Downloaded. We could not update the activity timestamp this time.",
        );
      }
    } catch (error) {
      setExportError(
        error instanceof Error
          ? error.message
          : "Full Blueprint download is unavailable.",
      );
    }
  }

  async function handleDownloadPdf() {
    if (incompletePrompt.incomplete) {
      setPdfError(
        "This Master Build Prompt appears incomplete. Regenerate it before exporting the PDF.",
      );
      return;
    }
    if (!canExportPdf) {
      setPdfError("PDF export is included with Pro.");
      return;
    }

    setPdfState("loading");
    setPdfError(null);

    try {
      const token = await getToken();
      const blob = await downloadBlueprintPdf(token, blueprintId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = createBlueprintMarkdownFileName(title).replace(
        /\.md$/,
        ".pdf",
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setPdfState("idle");
    } catch (error) {
      setPdfState("error");
      setPdfError(
        error instanceof Error
          ? error.message
          : "PDF export is unavailable right now.",
      );
    }
  }

  async function handleSubmitRating() {
    if (!selectedRating) {
      return;
    }

    setRatingState("saving");

    try {
      const token = await getToken();
      const response = await rateBlueprint(token, blueprintId, {
        rating: selectedRating,
        feedback,
      });

      onBlueprintUpdated?.(response.blueprint);
      setRatingState("saved");
    } catch {
      setRatingState("error");
    }
  }

  return (
    <div className="min-w-0 max-w-full space-y-6 overflow-x-hidden">
      {/* ================================================================
          HEADER PANEL
      ================================================================ */}
      <section className="overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-white via-indigo-50/70 to-violet-50 p-5 shadow-lg sm:p-7">
        <div>
          <p
            className={`text-sm font-semibold ${incompletePrompt.incomplete ? "text-red-700" : "text-indigo-700"}`}
          >
            {incompletePrompt.incomplete
              ? "Action required"
              : "Build-ready result"}
          </p>
          <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950 sm:text-3xl">
            {incompletePrompt.incomplete
              ? "Build Prompt Needs Regeneration"
              : "Your Blueprint Is Ready"}
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 sm:text-base">
            {incompletePrompt.incomplete
              ? "The saved Master Build Prompt appears incomplete. Regenerate before using it."
              : "Review the solution overview first, then use the Master Build Prompt to build your app."}
          </p>

          {metadata.length > 0 ? (
            <div className="mt-5 rounded-xl border border-white/80 bg-white/70 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Build details
              </p>
              <dl
                className="mt-2 grid min-w-0 divide-y divide-slate-200/70 sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-5"
                data-testid="build-details-row"
              >
                {metadata.map((item, index) => (
                  <div
                    key={item.label}
                    className={`min-w-0 py-2 first:pt-0 last:pb-0 sm:px-4 sm:py-0 sm:first:pl-0 sm:last:pr-0 ${
                      index > 0 ? "sm:border-l sm:border-slate-200/70" : ""
                    }`}
                  >
                    <dt className="text-xs text-slate-500">{item.label}</dt>
                    <dd className="mt-0.5 break-words text-sm font-semibold text-slate-900">
                      {item.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}
        </div>

        {incompletePrompt.incomplete ? (
          <div className="mt-6 grid gap-2 sm:flex sm:flex-wrap">
            <button
              type="button"
              onClick={() => void onRegenerate?.()}
              disabled={!onRegenerate || isRegenerating}
              className="w-full rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto"
            >
              {isRegenerating
                ? "Regenerating Blueprint..."
                : "Regenerate Blueprint"}
            </button>
            <Link
              to={`/blueprints/${blueprintId}?view=idea`}
              className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-center text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto"
            >
              View App Idea
            </Link>
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center">
            <Link
              to={`/blueprints/${blueprintId}?view=idea`}
              className="w-full rounded-lg border border-indigo-200 bg-white px-4 py-2.5 text-center text-sm font-semibold text-indigo-800 hover:bg-indigo-50 sm:w-auto"
            >
              View App Idea
            </Link>
          </div>
        )}

        <p className="mt-3 text-xs leading-5 text-slate-500">
          Follow the training journey: App Idea {"\u2192"} Confirmed Assumptions{" "}
          {"\u2192"} Blueprint {"\u2192"} Master Build Prompt {"\u2192"}{" "}
          Build/Test.
        </p>

        {incompletePrompt.incomplete ? (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            This saved Master Build Prompt appears incomplete. Copy and export
            actions are disabled; regenerate the blueprint before using it.
          </div>
        ) : null}

        {exportError ? (
          <p className="mt-3 text-sm text-red-700">{exportError}</p>
        ) : null}

        {trackingWarning ? (
          <p className="mt-3 text-xs leading-5 text-amber-700">
            {trackingWarning}
          </p>
        ) : null}
        {pdfError ? (
          <p className="mt-3 text-xs leading-5 text-amber-700">{pdfError}</p>
        ) : null}
      </section>

      {/* ================================================================
          TABS SECTION
      ================================================================ */}
      <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 pt-4 sm:px-6">
          <div
            role="tablist"
            aria-label="Generated blueprint sections"
            className="grid max-w-full gap-2 pb-3 sm:grid-cols-3"
            data-testid="blueprint-tabs-scroll"
          >
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              const isLocked = isTabLocked(tab.id);

              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tab.id)}
                  className={`min-w-0 rounded-xl border px-3 py-3 text-left transition ${
                    isActive
                      ? "border-indigo-300 bg-indigo-50 text-indigo-950 shadow-sm"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                  }`}
                >
                  <span className="flex min-w-0 items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-sm font-semibold">
                      {tab.label}
                    </span>
                    {isLocked ? (
                      <span
                        className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold ${
                          isActive
                            ? "bg-indigo-100 text-indigo-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        Pro
                      </span>
                    ) : null}
                  </span>
                  <span className="mt-1 block truncate text-xs font-medium text-slate-500">
                    {tab.description}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-5 sm:p-6">
          {/* ---- BLUEPRINT ---- */}
          {activeTab === "summary" ? (
            <div className="space-y-6" data-testid="blueprint-summary-section">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <SectionHeading
                  title="Blueprint"
                  description="Solution overview for your app."
                />
                <div
                  className="grid w-full shrink-0 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center"
                  data-testid="blueprint-download-actions"
                >
                  <button
                    type="button"
                    onClick={() => void handleDownloadMarkdown()}
                    disabled={incompletePrompt.incomplete}
                    className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto"
                  >
                    Download Blueprint
                  </button>
                  {canExportPdf ? (
                    <button
                      type="button"
                      onClick={() => void handleDownloadPdf()}
                      disabled={
                        pdfState === "loading" || incompletePrompt.incomplete
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 sm:w-auto"
                    >
                      {pdfState === "loading"
                        ? "Preparing PDF..."
                        : "Download PDF"}
                    </button>
                  ) : null}
                </div>
              </div>

              <div
                className="grid min-w-0 gap-4 md:grid-cols-2"
                data-testid="blueprint-five-part-grid"
              >
                <article
                  className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                  aria-label="01 Purpose"
                >
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                      01
                    </span>
                    <h4 className="text-sm font-semibold text-slate-950">
                      Purpose
                    </h4>
                  </div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Who the app is for, what problem it solves, and what outcome
                    it should create.
                  </p>
                  <BlueprintPreviewText
                    text={output.plainEnglishSummary}
                    maxItems={3}
                  />
                </article>
                <article
                  className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                  aria-label="02 Core Features"
                >
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                      02
                    </span>
                    <h4 className="text-sm font-semibold text-slate-950">
                      Core Features
                    </h4>
                  </div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    The 3 to 5 must-have Version 1 capabilities, kept focused.
                  </p>
                  <BlueprintPreviewText
                    text={output.vibeRcSpec.behavior}
                    mode="bullets"
                    maxItems={5}
                  />
                </article>
                <article
                  className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                  aria-label="03 User Flow"
                >
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                      03
                    </span>
                    <h4 className="text-sm font-semibold text-slate-950">
                      User Flow
                    </h4>
                  </div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    How the user moves through the app from first action to
                    useful result.
                  </p>
                  <BlueprintPreviewText
                    text={output.vibeRcSpec.examples}
                    mode="steps"
                    maxItems={6}
                  />
                </article>
                <article
                  className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                  aria-label="04 Visual Direction"
                >
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                      04
                    </span>
                    <h4 className="text-sm font-semibold text-slate-950">
                      Visual Direction
                    </h4>
                  </div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    How the app should look, feel, stack on mobile and guide the
                    user.
                  </p>
                  <BlueprintPreviewText
                    text={output.vibeRcSpec.interface}
                    mode="bullets"
                    maxItems={5}
                  />
                </article>
                <article
                  className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:col-span-2"
                  aria-label="05 Constraints"
                >
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                      05
                    </span>
                    <h4 className="text-sm font-semibold text-slate-950">
                      Constraints
                    </h4>
                  </div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    What is intentionally out of scope and should not be added
                    in Version 1.
                  </p>
                  <BlueprintPreviewText
                    text={constraintsPreviewText}
                    mode="bullets"
                    maxItems={5}
                  />
                </article>
              </div>

              <p className="text-xs leading-5 text-slate-500">
                Download the Blueprint for the full planning/review document.
                Download PDF when you want a human-readable copy for reading,
                sharing or saving.
              </p>
            </div>
          ) : null}

          {/* ---- TESTING NOTES ---- */}
          {activeTab === "testingNotes" ? (
            <div className="space-y-6" data-testid="testing-notes-section">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <SectionHeading
                  title="Testing Notes"
                  description="Use these checks before accepting the build. The full testing detail is included in the Master Build Prompt. Download or copy only the QA notes for handoff."
                />
                <div
                  className="grid w-full shrink-0 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center"
                  data-testid="testing-notes-actions"
                >
                  <button
                    type="button"
                    onClick={handleDownloadTestingNotes}
                    disabled={incompletePrompt.incomplete}
                    className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto"
                  >
                    {testingNotesDownloadState === "downloaded"
                      ? "Testing Notes Downloaded"
                      : testingNotesDownloadState === "error"
                        ? "Download Failed"
                        : "Download Testing Notes"}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleCopyTestingNotes()}
                    disabled={incompletePrompt.incomplete}
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 sm:w-auto"
                  >
                    {testingNotesCopyState === "copied"
                      ? "Copied"
                      : testingNotesCopyState === "error"
                        ? "Copy Failed"
                        : "Copy Testing Notes"}
                  </button>
                </div>
              </div>
              {testingNotesCopyState === "error" ? (
                <p className="text-sm text-red-700">
                  Testing Notes copy is unavailable. Please download the notes
                  instead.
                </p>
              ) : null}
              <div className="grid min-w-0 gap-4 lg:grid-cols-3">
                <article className="min-w-0 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <h4 className="text-sm font-semibold text-emerald-950">
                    Acceptance Criteria
                  </h4>
                  <div className="mt-3">
                    {isTabLocked("acceptance") ? (
                      <p className="text-sm leading-6 text-emerald-900">
                        Detailed acceptance criteria are included with Pro.
                      </p>
                    ) : (
                      <ConcisePreviewList
                        items={output.acceptanceCriteria}
                        maxItems={3}
                      />
                    )}
                  </div>
                  <p className="mt-3 text-xs font-medium text-emerald-800">
                    + more in the Master Build Prompt and Blueprint.
                  </p>
                </article>
                <article className="min-w-0 rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <h4 className="text-sm font-semibold text-amber-950">
                    Edge Cases
                  </h4>
                  <div className="mt-3">
                    {isTabLocked("edgeCases") ? (
                      <p className="text-sm leading-6 text-amber-900">
                        Detailed edge cases are included with Pro.
                      </p>
                    ) : (
                      <ConcisePreviewList
                        items={output.edgeCases}
                        maxItems={3}
                      />
                    )}
                  </div>
                  <p className="mt-3 text-xs font-medium text-amber-800">
                    + more in the Master Build Prompt and Blueprint.
                  </p>
                </article>
                <article className="min-w-0 rounded-xl border border-indigo-200 bg-indigo-50 p-4">
                  <h4 className="text-sm font-semibold text-indigo-950">
                    Test Scenarios
                  </h4>
                  <div className="mt-3">
                    {isTabLocked("tests") ? (
                      <p className="text-sm leading-6 text-indigo-900">
                        Detailed test scenarios are included with Pro.
                      </p>
                    ) : (
                      <ConcisePreviewList
                        items={output.testScenarios}
                        maxItems={3}
                      />
                    )}
                  </div>
                  <p className="mt-3 text-xs font-medium text-indigo-800">
                    + more in the Master Build Prompt and Blueprint.
                  </p>
                </article>
              </div>
            </div>
          ) : null}

          {/* ---- OVERVIEW ---- */}
          {activeTab === "overview" ? (
            <div className="space-y-8">
              <TabGuidance>
                Start here to confirm the app direction, target user, key
                assumptions and what is intentionally left out of Version 1.
              </TabGuidance>

              <div>
                <SectionHeading
                  title="Plain-English Summary"
                  description="A practical read on what the app does, who it serves, and what Version 1 should accomplish."
                />
                <div className="mt-4 rounded-md bg-slate-50 p-5">
                  <TextBlock>{output.plainEnglishSummary}</TextBlock>
                </div>
              </div>

              <div>
                <SectionHeading
                  title="Assumptions"
                  description="These are the ground rules the blueprint is using. If any assumption is wrong, adjust your app idea and regenerate before building."
                  count={output.assumptions.length}
                  countLabel="assumptions"
                />
                <div className="mt-4">
                  <ListSection items={output.assumptions} />
                </div>
              </div>

              {output.wontBuildYet.length > 0 ? (
                <div>
                  <SectionHeading
                    title="Not in Version 1"
                    description="These items are intentionally left out of the first build so your MVP stays focused and easier to complete."
                    count={output.wontBuildYet.length}
                    countLabel="deferred"
                  />
                  <div className="mt-4">
                    <ListSection items={output.wontBuildYet} />
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* ---- PRODUCT SPEC ---- */}
          {activeTab === "vibe" ? (
            <div className="space-y-6">
              <TabGuidance>
                Review this before using the Master Build Prompt. It explains
                what the app should do, how it should work, and what should stay
                out of Version 1.
              </TabGuidance>
              <SectionHeading
                title="Product Specification"
                description="A practical specification of what the app should do, how it should work, what data it needs, and what should stay out of Version 1."
              />
              <div className="grid gap-5">
                {vibeSections.map((section) => (
                  <div
                    key={section.key}
                    className="rounded-md border border-slate-200 bg-white p-5 shadow-sm"
                  >
                    <h4 className="text-sm font-bold text-slate-900">
                      {section.label}
                    </h4>
                    <p className="mt-0.5 text-xs leading-5 text-slate-500">
                      {section.description}
                    </p>
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      {section.key === "interface" ? (
                        <ScreenList text={output.vibeRcSpec[section.key]} />
                      ) : (
                        <TextBlock>{output.vibeRcSpec[section.key]}</TextBlock>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* ---- DATA MODEL ---- */}
          {activeTab === "data" ? (
            <div className="space-y-5">
              <TabGuidance>
                Use this when setting up the database or asking your AI builder
                to create tables, fields, statuses, validation rules and sample
                records.
              </TabGuidance>
              <div>
                <div className="flex items-start justify-between gap-4">
                  <SectionHeading
                    title="Data Model"
                    description="The key objects, fields, statuses, validation rules, and sample data needed for the MVP."
                    count={output.dataContentNeeded.length}
                    countLabel="items"
                  />
                  {output.dataContentNeeded.length > 0 ? (
                    <CopySectionButton
                      text={output.dataContentNeeded.join("\n\n")}
                    />
                  ) : null}
                </div>
                <div className="mt-4">
                  <DataModelList items={output.dataContentNeeded} />
                </div>
              </div>
            </div>
          ) : null}

          {/* ---- ACCEPTANCE CRITERIA ---- */}
          {activeTab === "acceptance" ? (
            <div className="space-y-5">
              <TabGuidance>
                Use these as pass/fail checks. Your first build is not done
                until these behaviours work.
              </TabGuidance>
              <div>
                <div className="flex items-start justify-between gap-4">
                  <SectionHeading
                    title="Acceptance Criteria"
                    description="Specific, testable checks that define whether the first build is working correctly."
                    count={
                      !isTabLocked("acceptance")
                        ? output.acceptanceCriteria.length
                        : undefined
                    }
                    countLabel="checks"
                  />
                  {!isTabLocked("acceptance") &&
                  output.acceptanceCriteria.length > 0 ? (
                    <CopySectionButton
                      text={output.acceptanceCriteria.join("\n\n")}
                    />
                  ) : null}
                </div>
                <div className="mt-4">
                  {isTabLocked("acceptance") ? (
                    <LockedProSection description="This Pro section gives you a pass/fail checklist of exactly what your MVP needs to do before you call it done. Share these with your builder so both of you know what 'complete' looks like." />
                  ) : (
                    <ChecklistList items={output.acceptanceCriteria} />
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* ---- EDGE CASES ---- */}
          {activeTab === "edgeCases" ? (
            <div className="space-y-5">
              <TabGuidance>
                Use these to prevent obvious breakages. Share them with your
                builder so the app handles awkward or unexpected situations
                gracefully.
              </TabGuidance>
              <div>
                <div className="flex items-start justify-between gap-4">
                  <SectionHeading
                    title="Edge Cases"
                    description="The awkward situations your app should handle gracefully — from empty inputs to failed saves and unexpected user actions."
                    count={
                      !isTabLocked("edgeCases")
                        ? output.edgeCases.length
                        : undefined
                    }
                    countLabel="cases"
                  />
                  {!isTabLocked("edgeCases") && output.edgeCases.length > 0 ? (
                    <CopySectionButton text={output.edgeCases.join("\n\n")} />
                  ) : null}
                </div>
                <div className="mt-4">
                  {isTabLocked("edgeCases") ? (
                    <LockedProSection description="This Pro section highlights the awkward scenarios your app needs to handle before real users find them — empty states, failed saves, duplicate actions, missing data and layout issues on narrow screens." />
                  ) : (
                    <EdgeCaseList items={output.edgeCases} />
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* ---- TEST SCENARIOS ---- */}
          {activeTab === "tests" ? (
            <div className="space-y-5">
              <TabGuidance>
                Run these scenarios before calling the MVP complete. Each one
                helps verify that the build works in real situations.
              </TabGuidance>
              <div>
                <div className="flex items-start justify-between gap-4">
                  <SectionHeading
                    title="Test Scenarios"
                    description="Step-by-step validation scenarios to run through before calling the MVP complete."
                    count={
                      !isTabLocked("tests")
                        ? output.testScenarios.length
                        : undefined
                    }
                    countLabel="scenarios"
                  />
                  {!isTabLocked("tests") && output.testScenarios.length > 0 ? (
                    <CopySectionButton
                      text={output.testScenarios.join("\n\n")}
                    />
                  ) : null}
                </div>
                <div className="mt-4">
                  {isTabLocked("tests") ? (
                    <LockedProSection description="This Pro section gives you practical test scenarios to run through before handing your app to real users. Each scenario includes a setup, an action and the expected result." />
                  ) : (
                    <TestScenarioList items={output.testScenarios} />
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* ---- BUILD PROMPT ---- */}
          {activeTab === "prompt" ? (
            <div className="space-y-5">
              <div className="space-y-3">
                <SectionHeading
                  title="Master Build Prompt"
                  description="Paste this into Replit, Lovable, Bolt, Cursor, or your preferred AI builder."
                />
                <div
                  className="grid w-full gap-2 sm:flex sm:flex-wrap"
                  data-testid="master-prompt-actions"
                >
                  <button
                    type="button"
                    onClick={handleDownloadBuildPrompt}
                    disabled={incompletePrompt.incomplete}
                    className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto"
                  >
                    Download Master Build Prompt
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleCopyBuildPrompt()}
                    disabled={incompletePrompt.incomplete}
                    className="w-full rounded-lg border border-indigo-200 bg-white px-4 py-2.5 text-sm font-semibold text-indigo-800 transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:text-slate-400 sm:w-auto"
                  >
                    {copyState === "copied"
                      ? "Copied"
                      : copyState === "error"
                        ? "Copy failed"
                        : "Copy Master Build Prompt"}
                  </button>
                  {copyState === "error" ? (
                    <p className="text-xs text-red-700">
                      Your browser blocked clipboard access.
                    </p>
                  ) : null}
                </div>
              </div>

              <div
                className="flex min-w-0 flex-wrap items-center gap-2"
                data-testid="sections-included-compact"
              >
                <p className="mr-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Sections included
                </p>
                <ul className="flex min-w-0 flex-wrap gap-1.5 text-xs text-slate-600">
                  {[
                    "Vision",
                    "Interface",
                    "Behavior",
                    "Examples",
                    "Role",
                    "Constraints",
                    "Acceptance Criteria",
                    "Edge Cases",
                    "Test Scenarios",
                    "Final Delivery",
                  ].map((section) => (
                    <li
                      key={section}
                      className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1"
                    >
                      {section}
                    </li>
                  ))}
                </ul>
              </div>

              {isProOutput ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-900">
                  <span className="font-semibold">Pro blueprint included.</span>{" "}
                  This prompt contains acceptance criteria, edge cases and test
                  scenarios so your AI builder knows what "done" means before
                  you call the first build complete.
                </div>
              ) : null}

              <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm font-semibold text-slate-950">
                    Master Build Prompt Preview
                  </p>
                  <p className="text-xs text-slate-500">
                    First {BUILD_PROMPT_PREVIEW_CHARS.toLocaleString()}{" "}
                    characters
                  </p>
                </div>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  This preview shows the first 2,000 characters only. Download
                  or copy the Master Build Prompt to use the complete version in
                  your AI builder.
                </p>
                <p className="mt-2 text-xs font-semibold leading-5 text-amber-700">
                  Do not copy from this preview. Use the Download or Copy button
                  to get the full Master Build Prompt.
                </p>
                <div
                  className="mt-4 max-w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50 p-4 font-mono text-sm leading-7 text-slate-700"
                  data-testid="build-prompt-preview"
                >
                  <ol className="grid min-w-0 gap-0">
                    {[
                      ...buildPromptPreviewLines,
                      ...(output.builderReadyPrompt.length >
                      BUILD_PROMPT_PREVIEW_CHARS
                        ? [
                            "",
                            "[Preview ends here. Download or copy the Master Build Prompt for the complete version.]",
                          ]
                        : []),
                    ].map((line, index) => (
                      <li
                        key={`${index}-${line}`}
                        className="grid min-w-0 grid-cols-[2.5rem_minmax(0,1fr)] gap-3"
                      >
                        <span className="select-none text-right text-slate-400">
                          {index + 1}
                        </span>
                        <span className="min-w-0 whitespace-pre-wrap break-words">
                          {line || " "}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* ================================================================
          RATING
      ================================================================ */}
      <section className="mt-10 rounded-lg border border-slate-100 bg-white/60 p-3 shadow-sm sm:p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              How useful is this blueprint?
            </p>
            <h3 className="mt-1 text-sm font-semibold text-slate-900">
              How useful is this blueprint?
            </h3>
            <p className="mt-1 max-w-2xl text-xs leading-5 text-slate-500">
              A quick rating helps improve future blueprint quality. This is
              optional.
            </p>
          </div>

          {ratingState === "saved" && selectedRating ? (
            <div className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              Thanks for your feedback. Saved rating: {selectedRating}/5.
            </div>
          ) : null}
        </div>

        {ratingState !== "saved" ? (
          <div className="mt-4 grid gap-4">
            <div className="flex flex-wrap gap-2" aria-label="Rate blueprint">
              {[1, 2, 3, 4, 5].map((value) => {
                const isSelected = selectedRating === value;

                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setSelectedRating(value)}
                    className={`h-9 w-9 rounded-md text-sm font-semibold transition ${
                      isSelected
                        ? "bg-indigo-600 text-white"
                        : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                    }`}
                    aria-pressed={isSelected}
                  >
                    {value}
                  </button>
                );
              })}
            </div>

            <div>
              <label
                htmlFor="blueprint-rating-feedback"
                className="block text-xs font-medium text-slate-600"
              >
                Optional feedback
              </label>
              <textarea
                id="blueprint-rating-feedback"
                value={feedback}
                onChange={(event) => setFeedback(event.target.value)}
                maxLength={1000}
                rows={2}
                className="mt-2 w-full resize-y rounded-md border border-slate-200 bg-white/80 px-3 py-2 text-sm leading-6 text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                placeholder="What made this useful, or what was missing?"
              />
            </div>

            {ratingState === "error" ? (
              <p className="text-sm text-red-700">
                We could not save your rating this time. Please try again.
              </p>
            ) : null}

            <div>
              <button
                type="button"
                onClick={() => void handleSubmitRating()}
                disabled={!selectedRating || ratingState === "saving"}
                className="rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {ratingState === "saving" ? "Saving..." : "Submit rating"}
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
